import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import uniqueId from 'util/uniqueId';

/**
 * Simple interface for passing around a React key alongside selected files.
 */
export interface AIModalSelectedFile {
  key: string;
  file: File;
}

interface AIModalFileChooserProps {
  selectedFiles: Array<AIModalSelectedFile>;
  onSetSelectedFiles: (newFiles: Array<AIModalSelectedFile>) => void;
  messageText?: string;
  // If the modal is in a submitted state, we no longer prompt for files.
  isSubmitted?: boolean;
}

/**
 * File chooser component for use in AI-related modals.
 *
 * This component shows a relatively compact view and allows adding multiple files and
 * removing them.
 */
export default function AIModalFileChooser({
  selectedFiles,
  onSetSelectedFiles,
  messageText,
  isSubmitted,
}: AIModalFileChooserProps): JSX.Element {
  return (
    <>
      {!isSubmitted && (
        <>
          <span>{messageText ?? 'Attach any documents, spreadsheets, or presentations:'}</span>
          <FileChooser
            multiple={true}
            onSelectFiles={(...files: Array<File>) => {
              onSetSelectedFiles([...selectedFiles, ...files.map((file) => ({key: uniqueId('file'), file}))]);
            }}
            className={css(styles.fileChooser)}
          />
        </>
      )}
      {selectedFiles.length > 0 && (
        <div className={css(styles.fileListWrapper)}>
          <FileList>
            {selectedFiles.map((selectedFile) => (
              <FileRow
                key={selectedFile.key}
                file={selectedFile.file}
                onRequestRemove={() => {
                  onSetSelectedFiles(selectedFiles.filter((f) => f !== selectedFile));
                }}
              />
            ))}
          </FileList>
        </div>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  fileChooser: {
    // Override the default padding within the file chooser, which is awkwardly large for this use case.
    padding: 8,
  },
  fileListWrapper: {
    // Ugly hack: The global `table` class on FileList adds 17px margin-bottom. Offset that with a
    // negative margin so that this component isn't imposing its own margins and instead lets its
    // parent handle spacing.
    marginBottom: -17,
  },
});
